import { isStringEmpty } from '@mechhive/helpers';
import { useFingerprint } from '@mechhive/react';
import { createSeoAlternateLinks, createSeoCanonical, createSeoMeta } from '@mechhive/remix';
import { MemoryFileStorage } from '@mjackson/file-storage/memory';
import type { FileUpload } from '@mjackson/form-data-parser';
import { parseFormData } from '@mjackson/form-data-parser';
import { captureException } from '@sentry/react';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { data, Form, useNavigation } from 'react-router';
import { z } from 'zod';
import { zx } from 'zodix';
import type { RewarbleCreateTicketWithAttachmentsDto } from '~/api/dto/rewarble/RewarbleCreateTicketWithAttachmentsDto';
import { createRecaptchaApi } from '~/api/recaptcha.api.server';
import { createRewarbleApi } from '~/api/rewarble.api.server';
import { createStoryblokApi } from '~/api/storyblok.api.server';
import type { StoryblokDoc } from '~/api/types/storyblok/blocks/StoryblokDoc';
import type { StoryblokPageSupport } from '~/api/types/storyblok/pages/StoryblokPageSupport';
import { Button } from '~/components/Button';
import { Recaptcha } from '~/components/Recaptcha';
import { FAQ, FAQItem } from '~/components/content/FAQ';
import { InputFileAttachment } from '~/components/form/InputFileAttachment';
import { InputSelect } from '~/components/form/InputSelect';
import { InputText } from '~/components/form/InputText';
import { InputTextArea } from '~/components/form/InputTextArea';
import { StoryblokRichText } from '~/components/storyblok/StoryblokRichText';
import { SubTitle } from '~/components/typograph/SubTitle';
import { Title } from '~/components/typograph/Title';
import { Icon, IconName } from '~/components/ui/Icon';
import { useLanguageServer } from '~/hooks/useLanguage.server';
import { useTranslate } from '~/hooks/useTranslate';
import { META_LANGUAGES } from '~/locale';
import { CheckmarkModal } from '~/modals/CheckmarkModal';
import { getSession } from '~/session.server';
import { createMetaUrl } from '~/utils/createMetaUrl';
import { getMetaBaseUrl } from '~/utils/getMetaBaseUrl';
import type { Route } from './+types/($lang).support._index';

export const loaderQuerySchema = z.object( {
  trx: z.string().optional()
} );

export const loader = async ( { request, params }: Route.LoaderArgs ) => {
  const loaderQuery = zx.parseQuery( request, loaderQuerySchema );
  const session = await getSession( request.headers.get( 'Cookie' ) );
  const language = useLanguageServer( params );
  
  const storyblokApi = createStoryblokApi();
  const [ storyResponse ] = await Promise.all( [
    storyblokApi.getStory<StoryblokPageSupport>( 'pages/support', language )
  ] );

  const authWrapper = session.get( 'authWrapper' );

  return {
    defaultEmail: authWrapper?.userAccount?.email,
    story: storyResponse.data.story.content,
    trx: loaderQuery.trx
  };
}

export const meta : Route.MetaFunction = ( { data, params, matches } ) => {
  const path = '/support';
  const baseUrl = getMetaBaseUrl( matches );
  const url = createMetaUrl( {
    baseUrl,
    path,
    lang: params.lang
  } );

  return [
    ...createSeoMeta( {
      title: data?.story.metaFields.title,
      description: data?.story.metaFields.description,
      og: {
        title: data?.story.metaFields.title,
        description: data?.story.metaFields.description,
        url,
        image: 'https://cdn.rewarble.com/images/static/feature-rewarble-image.jpg'
      }
    } ),
    createSeoCanonical( {
      url
    } ),
    ...createSeoAlternateLinks( {
      baseUrl,
      defaultLocale: 'en',
      path,
      locales: META_LANGUAGES.map( m => ( {
        locale: m.lang,
        pathPrefix: m.pathPrefix ?? ''
      } ) )
    } )
  ]
}

export const action = async ( { request, params }: Route.ActionArgs ) => {

  const allowedContentTypes = [
    'image/jpeg',   // .jpg, .jpeg
    'image/png',   // .png
    'image/gif',   // .gif
    'image/bmp',   // .bmp
    'image/tiff',   // .tiff, .tif
    'image/heic',   // .heic
    'image/heif',   // .heif
    'image/webp',   // .webp
    'image/svg+xml', // .svg
    'application/pdf', // .pdf (Portable Document Format)
    'text/plain',   // .txt (Plain Text)
    'application/rtf', // .rtf (Rich Text Format)
    'application/vnd.apple.pages', // .pages (Apple's word processing format)
    'application/vnd.apple.numbers', // .numbers (Apple's spreadsheet format)
    'application/vnd.apple.keynote', // .keynote (Apple's presentation format)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx (Word)
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx (Excel)
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx (PowerPoint)
    'application/vnd.oasis.opendocument.text', // .odt (OpenDocument Text)
    'application/vnd.oasis.opendocument.spreadsheet', // .ods (OpenDocument Spreadsheet)
    'application/vnd.oasis.opendocument.presentation', // .odp (OpenDocument Presentation)
    'video/mp4',   // .mp4
    'video/x-msvideo', // .avi
    'video/quicktime', // .mov
    'video/x-matroska', // .mkv
    'video/webm'   // .webm
  ];

  try {
    const storage = new MemoryFileStorage();
    const language = useLanguageServer( params );
    const uploadHandler = async ( fileUpload: FileUpload ) => {
      if ( 
        fileUpload.fieldName?.startsWith( 'file_' ) &&
        allowedContentTypes.includes( fileUpload.type )
      ) {
        const storageKey = fileUpload.fieldName;

        const file = await storage.put( storageKey, fileUpload as any );
        if ( file.size <= 20_000_000 ) {
          return storage.get( storageKey );
        }
      }
    };

    const formData = await parseFormData( request, uploadHandler );
    const recaptchaToken = formData.get( 'recaptchaToken' ) as string;

    const recaptchaApi = createRecaptchaApi();
    const verifyResponse = await recaptchaApi.verifyToken( recaptchaToken );
    if ( verifyResponse.data.success !== true ) {
      return data( {
        success: null,
        recaptchaError: true,
        error: false
      }, {
        status: 406
      } )
    }
    
    formData.set( 'language', language );

    const newFormData = new FormData();
    newFormData.set( 'createTicketDto', new Blob( [ JSON.stringify( {
      content: formData.get( 'content' ) as string,
      email: formData.get( 'email' ) as string,
      language,
      deviceFingerprint: formData.get( 'deviceFingerprint' ) as string,
      trx: formData.get( 'trx' ) as string,
      voucher: formData.get( 'voucher' ) as string,
      recaptchaChecked: true,
      subject: formData.get( 'subSubject' ) ? `${formData.get( 'subject' )}_${formData.get( 'subSubject' )}` : `${formData.get( 'subject' )}`,
    } satisfies RewarbleCreateTicketWithAttachmentsDto )], {
      type: 'application/json'
    } ) );
  
    formData.forEach( ( value, key ) => {
      if ( key.startsWith( 'file_' ) ){
        newFormData.append( 'attachments', value );
      }
    } );

    const rewarbleApi = createRewarbleApi( request );
    const response = await rewarbleApi.web.support.createTicketWithAttachments( newFormData );

    return {
      success: response.status === 201,
      recaptchaError: null,
      error: false
    }
  } catch ( ex ) {
    captureException( ex, {
      tags: {
        form: 'contact'
      }
    } );

    return data( {
      success: null,
      error: true,
      recaptchaError: null
    }, {
      status: 500
    } )
  }
};

export default function SupportPage ( { loaderData, actionData }: Route.ComponentProps ) {
  const navigation = useNavigation();
  const t = useTranslate();
  const { visitorId } = useFingerprint();
  const [ recaptchaToken, setRecaptchaToken ] = useState<string>( '' );
  const [ showCheckmarModal, setShowCheckmarkModal ] = useState<boolean>( false );
  const recaptchaRef = useRef<Recaptcha>( null );
  const [ subject, setSubject ] = useState<string>( '' );
  const [ subSubject, setSubSubject ] = useState<string>( '' );
  const [ forceShowMessageForm, setForceShowMessageForm ] = useState<boolean>();
  const [ forceCompleted, setForceCompleted ] = useState<boolean>();

  const subSubjects = useMemo( () => {
    return loaderData.story.subjects.find( s => s.value === subject )?.subSubjects ?? [];
  }, [ subject ] );

  const selectedSubSubject = useMemo( () => {
    return subSubjects.find( s => s.value === subSubject );
  }, [ subSubject ] );

  const handleSubmit = ( e: FormEvent<HTMLFormElement> ) => {
    if ( navigation.state === 'submitting' || isStringEmpty( recaptchaToken ) ) {
      e.preventDefault();
      return;
    }
  }

  useEffect( () => {
    if ( actionData?.success === true ) {
      window.scrollTo( {
        top: 0,
        behavior: 'smooth'
      } )
      setShowCheckmarkModal( true );
    }
  }, [actionData?.success] );

  useEffect( () => {
    if ( actionData != null && recaptchaRef.current ) {
      recaptchaRef.current.reset();
    }
  }, [ actionData ] );

  return(
    <div
      className={ 'container flex px-4 flex-col mx-auto items-center gap-[100px]' }>
      <div className={ 'flex flex-col items-center gap-8 max-w-[800px]' }>
        <div className={ 'flex flex-col gap-5 items-center w-full' }>
          <div className='flex flex-col gap-2.5'>
            <Title headingLevel={ 1 }>
              { actionData?.success === true ? loaderData.story.confirmFormTitle : forceCompleted === true ? loaderData.story.confirmFormAnsweredTitle : loaderData.story.title }
            </Title>
            <SubTitle className={ 'text-center' }>
              { actionData?.success === true ? loaderData.story.confirmFormSubTitle : forceCompleted === true ? loaderData.story.confirmFormAnsweredSubtitle : loaderData.story.subTitle }
            </SubTitle>
          </div>
          {
            ( actionData?.success === true || forceCompleted === true ) && (
              <div className='w-full flex justify-center'>    
                <Icon
                  name={ actionData?.success === true ? IconName.MailCheck : IconName.CircleCheck }
                  className='text-[90px]'></Icon>
              </div>
            )
          }
        </div>
        { ( actionData?.success !== true && forceCompleted !== true ) && (
          <div className='flex flex-col w-full md:max-w-[622px]'>
            <Form
              method={ 'POST' }
              className={ 'flex w-full flex-col' }
              onSubmit={ handleSubmit }
              encType='multipart/form-data'
            >
              <input
                type={ 'hidden' }
                name={ 'deviceFingerprint' }
                value={ visitorId }
              />
              <input
                type={ 'hidden' } 
                name={ 'recaptchaToken' } 
                value={ recaptchaToken }
              />
              <div className='flex flex-col gap-[30px] md:max-w-[622px] mb-16'>
                <InputText
                  title={  loaderData.story.inputEmailTitle  }
                  name={ 'email' }
                  type={ 'email' }
                  placeholder={ t( 'INPUT_ENTER_EMAIL_PLACEHOLDER' ) }
                  autoFocus={ loaderData.defaultEmail == null }
                  required={ true }
                  defaultValue={ loaderData.defaultEmail } />
                <InputText
                  title={ loaderData.story.inputVoucherTitle }
                  name={ 'voucher' }
                  type={ 'voucher' }
                  placeholder={ t( 'SUPPORT_INPUT_VOUCHER_PLACEHOLDER' ) }
                />
                <InputSelect 
                  title={ t( 'SUPPORT_INPUT_SUBJECT' ) }
                  value={ subject }
                  name={ 'subject' }
                  placeholder={ t( 'SUPPORT_INPUT_SUBJECT_PLACEHOLDER' ) }
                  options={ loaderData.story.subjects.map( ( subject ) => {
                    return (
                      {
                        value: subject.value,
                        label: subject.subject
                      }
                    )
                  } ) }
                  required={ true }
                  sortValues={ false }
                  onChange={ ( value )=> {
                    setSubject( value );
                    setForceShowMessageForm( false );
                  } }
                />
                { subSubjects.length > 0 &&
                  <InputSelect
                    title={ t( 'SUPPORT_INPUT_SUBSUBJECT' ) }
                    value={ subSubject }
                    name={ 'subSubject' }
                    placeholder={ t( 'SUPPORT_INPUT_SUBJECT_PLACEHOLDER' ) }
                    options={ subSubjects.map( ( subject ) => {
                      return (
                        {
                          value: subject.value,
                          label: subject.subject
                        }
                      )
                    } ) }
                    required={ true }
                    sortValues={ false }
                    onChange={ ( value )=> {
                      setSubSubject( value );
                      setForceShowMessageForm( false );
                    } }
                  />
                }
                { ( selectedSubSubject?.message?.content?.[0]?.content != null ) &&
                  <div className={ 'flex flex-col p-5 bg-[#0C102E80] border-white/20 border rounded-[10px]' }>
                    <h3 className={ 'font-semibold' }>Info</h3>
                    <StoryblokRichText>
                      { selectedSubSubject.message as StoryblokDoc }
                    </StoryblokRichText>
                    { selectedSubSubject.forceShowForm !== true &&
                      <div className={ 'grid md:grid-cols-2 gap-5' }>
                        <button
                          type={ 'button' }
                          className={ 'relative p-3 bg-[#0C102E99] border border-[#756BDE] shadow-[0px_-3.654px_9.136px_0px_rgba(255,255,255,0.20)_inset] rounded-[10px] overflow-hidden flex flex-col items-center gap-[30px]' }
                          onClick={ () => {      
                            window.scrollTo( {
                              top: 0,
                              behavior: 'smooth'
                            } )
                            setForceCompleted( true );
                          } }
                        >
                          <div className='absolute inset-x-[-100px] h-8 top-0 bg-[#756BDE66] rounded-full border border-[#756BDE] blur-2xl opacity-50' />
                          <div className={ 'flex-auto flex flex-col gap-2.5 items-center' }>
                            <Icon
                              className={ 'icon-size-6' }
                              name={ IconName.CircleCheck } 
                            />
                            <p className={ 'text-sm text-center text-pretty font-semibold' }>My question is answered</p>
                          </div>
                        </button>
                        <button
                          type={ 'button' }
                          className={ 'relative p-3 bg-[#0C102E99] border border-[#756BDE] shadow-[0px_-3.654px_9.136px_0px_rgba(255,255,255,0.20)_inset] rounded-[10px] overflow-hidden flex flex-col items-center gap-[30px]' }
                          onClick={ () => {
                            setForceShowMessageForm( true )
                          } }
                        >
                          <div className='absolute inset-x-[-100px] h-8 top-0 bg-[#756BDE66] rounded-full border border-[#756BDE] blur-2xl opacity-50' />
                          <div className={ 'flex-auto flex flex-col gap-2.5 items-center' }>
                            <Icon
                              className={ 'icon-size-6' }
                              name={ IconName.MessageCircleDots } 
                            />
                            <p className={ 'text-sm text-center text-pretty font-semibold' }>Send a message</p>
                          </div>
                        </button>
                      </div>
                    }
                  </div> 
                }
                { ( selectedSubSubject?.forceShowForm === true || forceShowMessageForm || ( selectedSubSubject != null && ( selectedSubSubject?.message == null || selectedSubSubject.message?.content?.[0]?.content == null ) ) ) &&
                  <>
                    <InputTextArea 
                      title={ loaderData.story.inputMessageTitle }
                      autoFocus={ loaderData.defaultEmail != null }
                      name={ 'content' }
                      required={ true }
                    />
                    <InputFileAttachment />
                    <input
                      type={ 'hidden' }
                      value={ loaderData.trx }
                      name={ 'trx' }
                    />
                    <div className={ 'min-h-[78px]' }>
                      <Recaptcha
                        ref={ recaptchaRef }
                        invalid={ actionData?.recaptchaError === true }
                        onChange={ ( token ) => setRecaptchaToken( token ) }
                      />
                    </div>
                    { actionData?.error === true &&
                      <p className={ 'text-center text-red-500 text-pretty px-4' }>{t( 'SUPPORT_FILEUPLOAD_ERROR_MESSAGE' )}</p>
                    }
                    <Button
                      type={ 'submit' }
                      disabled={ navigation.state !== 'idle' }
                      isLoading={ navigation.state !== 'idle' }>
                      {loaderData.story.submitButton}
                    </Button>
                  </>
                }
              </div>
            </Form>  
          </div>
        )
        }
      </div>
      <FAQ className={ 'md:max-w-[622px] w-full' }>
        { loaderData.story.faqItems.map( ( faqItem ) => {
          return (
            <FAQItem 
              key={ faqItem._uid }
              title={ faqItem.title }
              content={ faqItem.content as StoryblokDoc }
            />
          )
        } )}
      </FAQ>
      { showCheckmarModal &&
        <CheckmarkModal onComplete={ () => setShowCheckmarkModal( false ) } />
      }
    </div>
  )
}
